import { ViewType } from "pages/media/components/animation/Animation/";
import { ProjectType } from "pages/media/projects/types";
import type { OauthFlowAction, OrganizationSubscription } from "types";
import type { BaseSuperProperties } from "@libertify.frontend/analytics";
import { QuotaFeatures } from "constants/";

export type AuthMethod = "email" | "google" | "scalekit" | "apple";

/**
 * Super properties included in every event for the admin app.
 * @superProperties
 */
export type AppSuperProperties = BaseSuperProperties<"web_media_app">;

// might be an enum in the future
export type Plan = string;
export type PlanType = NonNullable<
  OrganizationSubscription["subscribed_products"]
>[0]["product_type"];
export type BillingInterval = OrganizationSubscription["billing_interval"];
export type DocumentInput = "file" | "prompt" | "picture" | "url";

export type UTMParams = {
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

/** First-touch UTMs and ICP type, read from the landing URL */
export type Attribution = {
  utm: UTMParams;
  icpType?: string;
};

/** User successfully submits create organization form
 * @backend since organization is created in backend
 */
type OrganizationCreatedEvent = {
  /** Whether this is the user's first organization */
  is_first_organization?: boolean;
  /** Organization ID */
  organization_id: string;
};

/** User clicks project menu button
 * @deprecated since new project page was introduced in 11/2025
 */
type ProjectMenuClickEvent = {
  /** Project ID */
  project_id: string;
};

/** User approves project settings */
type ProjectSettingsApproveEvent = {
  /** Project ID */
  project_id: string;
};

/** User attempts to log in (fired before authentication completes) */
type LoginAttemptEvent = {
  /** Login method used */
  login_method?: AuthMethod;
  /** Action performed during login attempt */
  action?: OauthFlowAction;
  /** Number of attempts in the current session (resets on page reload) */
  attempt_count?: number;
};

/** User logs in to the application */
type LoginEvent = {
  /** Login method used */
  login_method?: AuthMethod;
};

/** Login attempt failed */
type LoginFailedEvent = {
  /** Login method used */
  login_method?: AuthMethod;
  /** Reason for the failure */
  reason?: "invalid_credentials" | "network_error" | "oauth_error" | "unknown";
  /** Error type */
  error_type?:
    | "no_session_token"
    | "email_account_exists"
    | "account_locked"
    | "unknown";
};

/** User logs out from the application */
type LogoutEvent = Record<string, never>;

/** User initiates signup process with the first step of the signup wizard */
type SignupStartEvent = UserProperties;

type UserProperties = {
  /** Method used for signup */
  signup_method?: AuthMethod;
};

/** User submits signup form successfully and his account is created in backend*/
type SignupCompleteEvent = {
  /** User's email address */
  email: string;
} & UserProperties;

/** User deletes their account */
type UserDeletedEvent = Record<string, never>;

/** User changes view in storyboard (storyline) editor */
type StoryboardViewChangeEvent = {
  /** Project ID */
  project_id: string;
  /** Current view mode */
  storyboard_view: string;
  /** Storyboard ID */
  storyboard_id: string;
  /** Number of sections in storyboard */
  storyboard_sections_count: number;
  /** How the event was triggered */
  event_trigger: "page_view" | "click";
};

/** User adds chapter to storyline */
type StoryboardChapterAddEvent = {
  /** Project ID */
  project_id: string;
  /** Storyboard ID */
  storyboard_id: string;
};

/** User deletes chapter from storyboard */
type StoryboardChapterDeleteEvent = {
  /** Project ID */
  project_id: string;
  /** Storyboard ID */
  storyboard_id: string;
};

/** User adds section to storyboard */
type StoryboardSectionAddEvent = {
  /** Project ID */
  project_id: string;
  /** Storyboard ID */
  storyboard_id: string;
  /** Total number of sections */
  storyboard_sections_count: number;
  /** Index of the added section */
  storyboard_section_index: number;
};

/** User requests media generation from storyboard or video editor*/
export type StoryboardMediaGenerateEvent = {
  /** Project ID */
  project_id: string;
  /** Storyboard ID */
  storyboard_id: string;
  /** Page where generation was initiated */
  page: "Video Editor" | "Storyboard";
};

/** User sends prompt for storyboard section */
type StoryboardPromptSendEvent = {
  /** Storyboard ID */
  storyboard_id: string;
  /** Total number of sections */
  storyboard_sections_count: number;
  /** Index of the section */
  storyboard_section_index: number;
  /** Length of the prompt text */
  prompt_length: number;
  /** Target type */
  target: "section";
};

/** User focuses on storyboard prompt input */
type StoryboardPromptFocusEvent = {
  /** Storyboard ID */
  storyboard_id: string;
  /** Total number of sections */
  storyboard_sections_count: number;
  /** Index of the section */
  storyboard_section_index: number;
  /** Target type */
  target: "section";
};

/** User deletes section from storyboard */
type StoryboardSectionDeleteEvent = {
  /** Project ID */
  project_id: string;
  /** Storyboard ID */
  storyboard_id: string;
  /** Index of deleted section */
  storyboard_section_index: number;
};

/** User requests project media regeneration
 * Currently only available from the project page
 */
type ProjectMediaRegenerateEvent = {
  /** Project ID */
  project_id: string;
  /** Current status of media */
  media_status: string;
  /** Media ID */
  media_id: string;
};

/** User creates new project */
type ProjectStartEvent = {
  /** Project ID */
  project_id: string;
  /** Document format */
  document_format: string;
  /** Type of project */
  project_type: ProjectType;
  /** Input type of the document used for project creation */
  document_input: DocumentInput;
  /** Whether this is first project created in the current organization */
  is_first_project?: boolean;
  /** Number of projects in the current organization */
  project_number: number;
  /** Paywall experiment variant */
  paywall_flow?: string;
};

/** User initiates project creation
 * Triggered when user clicks on "Create Project" button in the project list page
 */
type ProjectCreateInitEvent = {
  /** Type of project to create (optional when not yet known) */
  project_type?: ProjectType;
  /** Page where creation was initiated */
  page: "project_creation_wizard" | "project_list" | "home";
  /** Paywall experiment variant */
  paywall_flow?: string;
};

/** User deletes project successfully */
type ProjectDeleteEvent = {
  /** Project ID */
  project_id: string;
};

/** User updates project setting */
type ProjectSettingUpdateEvent = {
  /** Project ID */
  project_id: string;
  /** Name of the setting */
  setting_name: string;
  /** New value for the setting */
  setting_value: string;
  /** Page where update occurred */
  page: "project_creation_wizard" | "project_settings";
};

/** User completes project creation wizard step */
type ProjectWizardStepCompletedEvent = {
  /** Step number */
  step_number: number;
  /** Name of the step */
  step_name: string;
  /** Time spent on step in seconds */
  step_duration: number;
};

/** User publishes project experience */
type ProjectExperiencePublishEvent = {
  /** Project ID */
  project_id: string;
  /** Instance ID */
  instance_id: string;
};

/** User starts trial period */
type TrialStartEvent = Record<string, never>;

/** User creates brand kit */
type BrandkitCreateEvent = {
  /** Brand kit ID */
  brandkit_id?: string;
};

/** User saves brand kit */
type BrandkitSaveEvent = {
  /** Brand kit ID */
  brandkit_id?: string;
  /** Type of assets being saved */
  assets_type?: string;
};

/** User changes brand kit color */
type BrandKitColorChangeEvent = {
  /** Brand kit ID */
  brandkit_id?: string;
  /** Name of the color being changed */
  brandkit_color_name?: string;
};

/** User uploads asset to brand kit */
type BrandKitAssetUploadEvent = {
  /** Brand kit ID */
  brandkit_id?: string;
  /** Type of asset */
  asset_type?: string;
};

/** User deletes asset from brand kit */
type BrandKitAssetDeleteEvent = {
  /** Brand kit ID */
  brandkit_id?: string;
  /** Type of asset */
  asset_type?: string;
  /** Asset ID */
  asset_id?: string;
};

/** User creates voice clone successfully*/
type VoiceCloneCreateEvent = {
  /** Voice ID */
  voice_id: string;
  /** Number of audio samples */
  samples_count: number;
  /** Total duration of samples in seconds */
  samples_duration: number;
};

/** User creates face clone successfully*/
type FaceCloneCreateEvent = {
  /** Face profile ID */
  face_profile_id: string;
};

/** User creates CTA group successfully*/
type CtaGroupCreateEvent = {
  /** Number of CTAs in group */
  ctas_count: number;
};

/** User updates CTA group successfully*/
type CtaGroupUpdateEvent = {
  /** CTA group ID */
  cta_group_id: string;
  /** Number of CTAs in group */
  ctas_count: number;
};

/** User adds CTA */
type CtaAddEvent = {
  /** CTA ID */
  cta_id: string;
  /** Type of CTA */
  cta_type: string;
};

/** User deletes CTA */
type CtaDeleteEvent = {
  /** CTA ID */
  cta_id: string;
  /** Type of CTA */
  cta_type: string;
};

type HighlightSource = "user" | "system";
type DocumentFormat = "pdf" | "html";

/** User adds highlight to document animation */
type DocumentAnimationHighlightAddEvent = {
  /** Project ID */
  project_id: string;
  /** Type of highlight */
  highlight_type: string;
  /** Source of the highlight */
  highlight_source: HighlightSource;
  /** Document format */
  document_format: DocumentFormat;
};

/** User deletes highlight from document animation */
type DocumentAnimationHighlightDeleteEvent = {
  /** Project ID */
  project_id: string;
  /** Type of highlight */
  highlight_type: string;
  /** Source of the highlight */
  highlight_source: HighlightSource;
  /** Document format */
  document_format: DocumentFormat;
};

/** User modifies all document animations */
type DocumentAnimationModifyAllEvent = {
  /** Project ID */
  project_id: string;
};

/** User deletes all document animations */
type DocumentAnimationDeleteAllEvent = {
  /** Project ID */
  project_id: string;
};

/** User saves all document animations */
type DocumentAnimationSaveAllEvent = {
  /** Project ID */
  project_id: string;
  /** Number of updates saved */
  updates_count: number;
};

/** User copies storyboard script */
type StoryboardScriptCopyEvent = {
  /** Project ID */
  project_id: string;
  /** Type of copy operation */
  storyboard_copy_type: string;
};

/** User plays storyboard script text-to-speech */
type StoryboardScriptTtsPlayEvent = {
  /** Project ID */
  project_id: string;
};

/** User copies project sharing link */
type ProjectSharingLinkCopyEvent = {
  /** Project ID */
  project_id: string;
  /** Instance ID */
  instance_id: string;
};

/** User shares project experience to social media or copy the link */
type ProjectShareIntentEvent = {
  /** Project ID */
  project_id: string;
  /** Instance ID */
  instance_id: string;
  /** Sharing method */
  method: string;
  /** Paywall experiment variant */
  paywall_flow?: string;
};

/** User opens project experience sharing link */
export type ProjectSharingLinkOpenEvent = {
  /** Project ID */
  project_id: string;
  /** Instance ID */
  instance_id: string;
  /** Media status */
  media_status: string;
  /** Media ID */
  media_id?: string;
  /** Whether media is published */
  published: boolean;
  /** Page where link was opened */
  page?: "Project" | "Share Experience";
};

type OAuthProvider = "Google";
/** User logs in with OAuth provider */
type LoginWithOAuthEvent = {
  /** OAuth provider name */
  provider: OAuthProvider;
  action: OauthFlowAction;
};

/** User exports chat history */
type ChatHistoryExportEvent = {
  /** Project ID */
  project_id: string;
  /** Start date of export range */
  start_date: string;
  /** End date of export range */
  end_date: string;
};

/** User previews document animation */
type DocumentAnimationPreviewEvent = {
  /** Project ID */
  project_id: string;
  /** Type of project */
  project_type: ProjectType;
  /** View type */
  view_type: ViewType;
};

/** User starts subscription
 * @deprecated the backend triggers subscription_created event
 */
// plan: Plan; // TODO with backend
type SubscriptionStartEvent = Record<string, never>;
type PurchaseEvent = {
  /** Purchase value (Plan price used to give weights to plans in Google Ads)*/
  value: number;
  /** Purchase Currency (Plan currency) */
  currency: string;
  /** Transaction ID. Used by GA4 to deduplicate purchases */
  transaction_id: string;
  /** Product name */
  product_name?: string;
  /** Product ID */
  product_id?: string;
  /** Product type */
  product_type?: PlanType;
};
/** User successfully subscribes to a plan on Stripe
 * @backend subscription is created in backend after successful payment webhook
 */
type SubscriptionCreatedEvent = {
  stripe_customer_id: string | null;
  trial_end_date: string | null;
  trial_start_date: string | null;
  plan_end_date: string | null;
  plan_start_date: string | null;
  license_type: string;
  license_name: string;
  mrr: string;
  one_time_amount: string;
};
/** User clicks Create Playbook button to start defining a new playbook */
type SettingsPlaybookCreateEvent = Record<string, never>;

/** User saves new playbook */
type SettingsPlaybookSaveEvent = {
  /** Playbook ID */
  playbook_id: string;
  /** Playbook name */
  playbook_name: string;
};

/** User updates existing playbook */
type SettingsPlaybookUpdateEvent = {
  /** Playbook ID */
  playbook_id: string;
};

/** User views the paywall page */
type PaywallViewEvent = {
  /** Paywall experiment variant */
  paywall_flow?: string;
  /** Paywall variant scenario */
  paywall_scenario?: string;
  /** Whether the paywall is hard or soft */
  paywall_hard: boolean;
  /** Whether the user's trial has expired (hard_no_card_3d or soft_3_exp) */
  expired_trial?: boolean;
  /** Stripe pricing table ID shown to the user */
  pricing_table_id: string;
};

/** User dismisses paywall */
type PaywallDismissEvent = PaywallViewEvent;

/** User clicks an upgrade button */
type UpgradeButtonClickEvent = {
  /** Where the button is located in the UI */
  location?: string;
};

/** User clicks the Chrome Extension promo card on the home page */
type ChromeExtensionClickEvent = {
  /** Page where the click originated */
  page: "home";
};


type ExperimentExposureEvent = {
  /** Experiment name */
  experiment_name: string;
  /** Experiment variant */
  experiment_variant: string;
} & Record<string, unknown>; // allow experiment specific params

/** User sees pricing options (fired from any surface that renders a pricing UI) */
type PricingViewEvent = {
  /** UI container that holds the pricing UI */
  container:
    | "paywall"
    | "pricing_modal"
    | "soft_paywall_modal"
    | "pricing_page";
  /** Stripe pricing table ID */
  pricing_table_id: string;
};

/** User views quota upgrade modal */
type QuotaReachedViewEvent = {
  /** Quota feature */
  quota_feature: QuotaFeatures;
  /** Quota limit */
  quota_limit: number;
};

export enum AnalyticsEvents {
  /**
   * See {@link OrganizationCreatedEvent} for details
   * @see OrganizationCreatedEvent
   * @backend
   */
  OrganizationCreated = "organization_created",
  /**
   * See {@link ProjectSharingLinkOpenEvent} for details
   * @see ProjectSharingLinkOpenEvent
   */
  ProjectExperienceOpen = "project_experience_open",
  /**
   * See {@link ProjectMenuClickEvent} for details
   * @see ProjectMenuClickEvent
   * @deprecated since new project page was introduced in 11/2025
   */
  ProjectMenuClick = "project_menu_click",
  /**
   * See {@link ProjectSettingsApproveEvent} for details
   * @see ProjectSettingsApproveEvent
   */
  ProjectSettingsApprove = "project_settings_approve",
  /**
   * See {@link ProjectStartEvent} for details
   * @see ProjectStartEvent
   */
  ProjectStart = "project_start",
  /**
   * See {@link ProjectCreateInitEvent} for details
   * @see ProjectCreateInitEvent
   */
  ProjectCreateInit = "project_create_init",
  /**
   * See {@link ProjectDeleteEvent} for details
   * @see ProjectDeleteEvent
   */
  ProjectDelete = "project_delete",
  /**
   * See {@link ProjectSettingUpdateEvent} for details
   * @see ProjectSettingUpdateEvent
   */
  ProjectSettingUpdate = "project_setting_update",
  /**
   * See {@link ProjectWizardStepCompletedEvent} for details
   * @see ProjectWizardStepCompletedEvent
   */
  ProjectWizardStepCompleted = "project_wizard_step_completed",
  /**
   * See {@link LoginEvent} for details
   * @see LoginEvent
   */
  Login = "login",
  /**
   * See {@link LoginAttemptEvent} for details
   * @see LoginAttemptEvent
   */
  LoginAttempt = "login_attempt",
  /**
   * See {@link LoginFailedEvent} for details
   * @see LoginFailedEvent
   */
  LoginFailed = "login_failed",
  /**
   * See {@link LogoutEvent} for details
   * @see LogoutEvent
   * @todo tracking call not yet implemented
   */
  Logout = "logout",
  /**
   * See {@link SignupStartEvent} for details
   * @see SignupStartEvent
   */
  SignupStart = "signup_start",
  /**
   * See {@link SignupCompleteEvent} for details
   * @see SignupCompleteEvent
   */
  SignupComplete = "signup_complete",
  /**
   * See {@link UserProperties} for details
   * @see UserProperties
   * @deprecated DLY 2026-02-26 as we now use the shared user_activated event for all signup flows
   */
  UserRegistered = "user_registered",
  /**
   * When user account is activated after signup flow is fully completed
   * (email verified, OAuth signup completed)
   * See {@link UserProperties} for details
   * @see UserProperties
   * @minUsages 2  authProvider.ts (OAuth/social signup) + verification.tsx (email confirm)
   */
  UserActivated = "user_activated",
  /**
   * See {@link UserProperties} for details
   * @see UserProperties
   */
  EmailVerified = "email_verified",
  /**
   * See {@link UserProperties} for details
   * @see UserProperties
   */
  ProfileCompleted = "profile_completed",
  UserDeleted = "user_deleted",
  // FirstProjectCreated = "first_project_created",
  /**
   * See {@link StoryboardViewChangeEvent} for details
   * @see StoryboardViewChangeEvent
   */
  StoryboardViewChange = "storyboard_view_change",
  /**
   * See {@link StoryboardChapterAddEvent} for details
   * @see StoryboardChapterAddEvent
   * @todo tracking call not yet implemented
   */
  StoryboardChapterAdd = "storyboard_chapter_add",
  /**
   * See {@link StoryboardChapterDeleteEvent} for details
   * @see StoryboardChapterDeleteEvent
   * @todo tracking call not yet implemented
   */
  StoryboardChapterDelete = "storyboard_chapter_delete",
  /**
   * See {@link StoryboardMediaGenerateEvent} for details
   * @see StoryboardMediaGenerateEvent
   */
  StoryboardMediaGenerate = "storyboard_media_generate",
  /**
   * See {@link StoryboardSectionAddEvent} for details
   * @see StoryboardSectionAddEvent
   */
  StoryboardSectionAdd = "storyboard_section_add",
  /**
   * See {@link StoryboardSectionDeleteEvent} for details
   * @see StoryboardSectionDeleteEvent
   */
  StoryboardSectionDelete = "storyboard_section_delete",
  /**
   * See {@link StoryboardPromptFocusEvent} for details
   * @see StoryboardPromptFocusEvent
   */
  StoryboardPromptFocus = "storyboard_prompt_focus",
  /**
   * See {@link StoryboardPromptSendEvent} for details
   * @see StoryboardPromptSendEvent
   */
  StoryboardPromptSend = "storyboard_prompt_send",
  /**
   * See {@link TrialStartEvent} for details
   * @see TrialStartEvent
   * @deprecated
   */
  TrialStart = "trial_start",
  /**
   * See {@link SubscriptionStartEvent} for details
   * @see SubscriptionStartEvent
   * @deprecated the backend triggers subscription_created event
   */
  SubscriptionStart = "subscription_start",
  /**
   * See {@link PurchaseEvent} for details
   * We use the recommended event "purchase" from GA4, as it's simpler with Google Ads integration
   * @see PurchaseEvent
   */
  Purchase = "purchase",
  /**
   * See {@link SubscriptionCreatedEvent} for details
   * @see SubscriptionCreatedEvent
   * @backend
   */
  SubscriptionCreated = "subscription_created",
  /**
   * See {@link ProjectMediaRegenerateEvent} for details
   * @see ProjectMediaRegenerateEvent
   */
  ProjectMediaRegenerate = "project_media_regenerate",
  /**
   * See {@link BrandkitCreateEvent} for details
   * @see BrandkitCreateEvent
   */
  BrandKitCreate = "brandkit_create",
  /**
   * See {@link BrandkitSaveEvent} for details
   * @see BrandkitSaveEvent
   */
  BrandKitSave = "brandkit_save",
  /**
   * See {@link BrandKitColorChangeEvent} for details
   * @see BrandKitColorChangeEvent
   */
  BrandKitColorChange = "brandkit_color_change",
  /**
   * See {@link BrandKitAssetUploadEvent} for details
   * @see BrandKitAssetUploadEvent
   */
  BrandKitAssetUpload = "brandkit_asset_upload",
  /**
   * See {@link BrandKitAssetDeleteEvent} for details
   * @see BrandKitAssetDeleteEvent
   */
  BrandKitAssetDelete = "brandkit_asset_delete",
  /**
   * See {@link DocumentAnimationHighlightAddEvent} for details
   * @see DocumentAnimationHighlightAddEvent
   */
  DocumentAnimationHighlightAdd = "document_animation_highlight_add",
  /**
   * See {@link DocumentAnimationHighlightDeleteEvent} for details
   * @see DocumentAnimationHighlightDeleteEvent
   */
  DocumentAnimationHighlightDelete = "document_animation_highlight_delete",
  /**
   * See {@link DocumentAnimationModifyAllEvent} for details
   * @see DocumentAnimationModifyAllEvent
   */
  DocumentAnimationModifyAll = "document_animation_modify_all",
  /**
   * See {@link DocumentAnimationDeleteAllEvent} for details
   * @see DocumentAnimationDeleteAllEvent
   */
  DocumentAnimationDeleteAll = "document_animation_delete_all",
  /**
   * See {@link DocumentAnimationPreviewEvent} for details
   * @see DocumentAnimationPreviewEvent
   */
  DocumentAnimationPreview = "document_animation_preview",
  /**
   * See {@link StoryboardScriptCopyEvent} for details
   * @see StoryboardScriptCopyEvent
   */
  StoryboardScriptCopy = "storyboard_script_copy",
  /**
   * See {@link StoryboardScriptTtsPlayEvent} for details
   * @see StoryboardScriptTtsPlayEvent
   * @todo tracking call not yet implemented
   */
  StoryboardScriptTtsPlay = "storyboard_script_tts_play",
  /**
   * See {@link VoiceCloneCreateEvent} for details
   * @see VoiceCloneCreateEvent
   */
  VoiceCloneCreate = "voice_clone_create",
  /**
   * See {@link FaceCloneCreateEvent} for details
   * @see FaceCloneCreateEvent
   */
  FaceCloneCreate = "face_clone_create",
  /**
   * See {@link CtaGroupCreateEvent} for details
   * @see CtaGroupCreateEvent
   */
  CtaGroupCreate = "cta_group_create",
  /**
   * See {@link CtaGroupUpdateEvent} for details
   * @see CtaGroupUpdateEvent
   */
  CtaGroupUpdate = "cta_group_update",
  /**
   * See {@link CtaAddEvent} for details
   * @see CtaAddEvent
   */
  CtaAdd = "cta_add",
  /**
   * See {@link CtaDeleteEvent} for details
   * @see CtaDeleteEvent
   */
  CtaDelete = "cta_delete",
  /**
   * See {@link ProjectSharingLinkCopyEvent} for details
   * @see ProjectSharingLinkCopyEvent
   */
  ProjectSharingLinkCopy = "project_experience_link_copy",
  // ProjectSharingLinkOpen = "project_sharing_link_open",
  /**
   * See {@link ProjectExperiencePublishEvent} for details
   * @see ProjectExperiencePublishEvent
   */
  ProjectExperienceUnpublish = "project_experience_unpublish",
  /**
   * See {@link ProjectExperiencePublishEvent} for details
   * @see ProjectExperiencePublishEvent
   */
  ProjectExperiencePublish = "project_experience_publish",
  /**
   * See {@link ProjectShareIntent} for details
   * @see ProjectShareIntent
   */
  ProjectShareIntent = "project_share_intent",
  /**
   * See {@link LoginWithOAuthEvent} for details
   * @see LoginWithOAuthEvent
   * @deprecated since updated login events DLY 2026-03-18
   */
  LoginWithOAuth = "login_oauth",
  /**
   * See {@link DocumentAnimationSaveAllEvent} for details
   * @see DocumentAnimationSaveAllEvent
   */
  DocumentAnimationSaveAll = "document_animation_save_all",
  /**
   * See {@link ChatHistoryExportEvent} for details
   * @see ChatHistoryExportEvent
   */
  ChatHistoryExport = "chat_history_export",
  /**
   * See {@link SettingsPlaybookCreateEvent} for details
   * @see SettingsPlaybookCreateEvent
   */
  SettingsPlaybookCreate = "settings_playbook_create",
  /**
   * See {@link SettingsPlaybookSaveEvent} for details
   * @see SettingsPlaybookSaveEvent
   */
  SettingsPlaybookSave = "settings_playbook_save",
  /**
   * See {@link SettingsPlaybookUpdateEvent} for details
   * @see SettingsPlaybookUpdateEvent
   */
  SettingsPlaybookUpdate = "settings_playbook_update",
  /**
   * See {@link QuotaReachedViewEvent} for details
   * @see QuotaReachedViewEvent
   */
  QuotaReachedView = "quota_reached_view",
  /**
   * See {@link PaywallViewEvent} for details
   * @see PaywallViewEvent
   */
  PaywallView = "paywall_view",
    /**
   * See {@link PaywallDismissEvent} for details
   * @see PaywallDismissEvent
   */
  PaywallDismiss = "paywall_dismiss",
  /**
   * See {@link PricingViewEvent} for details
   * @see PricingViewEvent
   */
  PricingView = "pricing_view",
  /**
   * See {@link UpgradeButtonClickEvent} for details
   * @see UpgradeButtonClickEvent
   */
  UpgradeButtonClick = "upgrade_button_click",
  /**
   * See {@link ChromeExtensionClickEvent} for details
   * @see ChromeExtensionClickEvent
   */
  ChromeExtensionClick = "chrome_extension_click",
  /**
   * See {@link ExperimentExposureEvent} for details
   * @see ExperimentExposureEvent
   * @deprecated 2026-10-19 Statsig removed, no experiments run anymore (MED-7405)
   */
  ExperimentExposure = "experiment_exposure",
}
export type AnalyticsEventsMap = {
  [AnalyticsEvents.OrganizationCreated]: OrganizationCreatedEvent;
  [AnalyticsEvents.ProjectMenuClick]: ProjectMenuClickEvent;
  [AnalyticsEvents.ProjectSettingsApprove]: ProjectSettingsApproveEvent;
  [AnalyticsEvents.ProjectStart]: ProjectStartEvent;
  [AnalyticsEvents.ProjectCreateInit]: ProjectCreateInitEvent;
  [AnalyticsEvents.ProjectDelete]: ProjectDeleteEvent;
  [AnalyticsEvents.ProjectSettingUpdate]: ProjectSettingUpdateEvent;
  [AnalyticsEvents.ProjectWizardStepCompleted]: ProjectWizardStepCompletedEvent;
  [AnalyticsEvents.Login]: LoginEvent;
  [AnalyticsEvents.LoginAttempt]: LoginAttemptEvent;
  [AnalyticsEvents.LoginFailed]: LoginFailedEvent;
  [AnalyticsEvents.Logout]: LogoutEvent;
  [AnalyticsEvents.SignupStart]: SignupStartEvent;
  [AnalyticsEvents.SignupComplete]: SignupCompleteEvent;
  [AnalyticsEvents.UserRegistered]: UserProperties;
  [AnalyticsEvents.UserActivated]: UserProperties;
  [AnalyticsEvents.EmailVerified]: UserProperties;
  [AnalyticsEvents.ProfileCompleted]: UserProperties;
  [AnalyticsEvents.LoginWithOAuth]: LoginWithOAuthEvent;
  [AnalyticsEvents.StoryboardViewChange]: StoryboardViewChangeEvent;
  [AnalyticsEvents.StoryboardChapterAdd]: StoryboardChapterAddEvent;
  [AnalyticsEvents.StoryboardChapterDelete]: StoryboardChapterDeleteEvent;
  [AnalyticsEvents.StoryboardMediaGenerate]: StoryboardMediaGenerateEvent;
  [AnalyticsEvents.StoryboardSectionAdd]: StoryboardSectionAddEvent;
  [AnalyticsEvents.StoryboardSectionDelete]: StoryboardSectionDeleteEvent;
  [AnalyticsEvents.StoryboardPromptFocus]: StoryboardPromptFocusEvent;
  [AnalyticsEvents.StoryboardPromptSend]: StoryboardPromptSendEvent;
  [AnalyticsEvents.ProjectMediaRegenerate]: ProjectMediaRegenerateEvent;
  [AnalyticsEvents.TrialStart]: TrialStartEvent;
  [AnalyticsEvents.SubscriptionStart]: SubscriptionStartEvent;
  [AnalyticsEvents.Purchase]: PurchaseEvent;
  [AnalyticsEvents.SubscriptionCreated]: SubscriptionCreatedEvent;
  [AnalyticsEvents.BrandKitCreate]: BrandkitCreateEvent;
  [AnalyticsEvents.BrandKitSave]: BrandkitSaveEvent;
  [AnalyticsEvents.BrandKitColorChange]: BrandKitColorChangeEvent;
  [AnalyticsEvents.BrandKitAssetUpload]: BrandKitAssetUploadEvent;
  [AnalyticsEvents.BrandKitAssetDelete]: BrandKitAssetDeleteEvent;
  [AnalyticsEvents.VoiceCloneCreate]: VoiceCloneCreateEvent;
  [AnalyticsEvents.FaceCloneCreate]: FaceCloneCreateEvent;
  [AnalyticsEvents.CtaGroupCreate]: CtaGroupCreateEvent;
  [AnalyticsEvents.CtaGroupUpdate]: CtaGroupUpdateEvent;
  [AnalyticsEvents.CtaAdd]: CtaAddEvent;
  [AnalyticsEvents.CtaDelete]: CtaDeleteEvent;
  [AnalyticsEvents.DocumentAnimationHighlightAdd]: DocumentAnimationHighlightAddEvent;
  [AnalyticsEvents.DocumentAnimationHighlightDelete]: DocumentAnimationHighlightDeleteEvent;
  [AnalyticsEvents.DocumentAnimationModifyAll]: DocumentAnimationModifyAllEvent;
  [AnalyticsEvents.DocumentAnimationDeleteAll]: DocumentAnimationDeleteAllEvent;
  [AnalyticsEvents.StoryboardScriptCopy]: StoryboardScriptCopyEvent;
  [AnalyticsEvents.StoryboardScriptTtsPlay]: StoryboardScriptTtsPlayEvent;
  [AnalyticsEvents.ProjectSharingLinkCopy]: ProjectSharingLinkCopyEvent;
  [AnalyticsEvents.ProjectExperienceOpen]: ProjectSharingLinkOpenEvent;
  [AnalyticsEvents.ProjectShareIntent]: ProjectShareIntentEvent;
  [AnalyticsEvents.ProjectExperienceUnpublish]: ProjectExperiencePublishEvent;
  [AnalyticsEvents.ProjectExperiencePublish]: ProjectExperiencePublishEvent;
  [AnalyticsEvents.DocumentAnimationSaveAll]: DocumentAnimationSaveAllEvent;
  [AnalyticsEvents.ChatHistoryExport]: ChatHistoryExportEvent;
  [AnalyticsEvents.DocumentAnimationPreview]: DocumentAnimationPreviewEvent;
  [AnalyticsEvents.UserDeleted]: UserDeletedEvent;
  [AnalyticsEvents.SettingsPlaybookCreate]: SettingsPlaybookCreateEvent;
  [AnalyticsEvents.SettingsPlaybookSave]: SettingsPlaybookSaveEvent;
  [AnalyticsEvents.SettingsPlaybookUpdate]: SettingsPlaybookUpdateEvent;
  [AnalyticsEvents.QuotaReachedView]: QuotaReachedViewEvent;
  [AnalyticsEvents.PaywallView]: PaywallViewEvent;
  [AnalyticsEvents.PaywallDismiss]: PaywallDismissEvent;
  [AnalyticsEvents.PricingView]: PricingViewEvent;
  [AnalyticsEvents.UpgradeButtonClick]: UpgradeButtonClickEvent;
  [AnalyticsEvents.ChromeExtensionClick]: ChromeExtensionClickEvent;
  [AnalyticsEvents.ExperimentExposure]: ExperimentExposureEvent;
};
